import { AssignmentExtendedModel, AssignmentViewModel } from 'api/model';
import { useAssignmentGetImportantHook } from 'api/assignment/assignment';
import AssignmentsList from 'components/AssignmentsList';
import ErrorTryAgain from 'components/ErrorTryAgain';
import FilterPicker from 'components/filterBar/FilterPicker';
import FilterSearchInput from 'components/filterBar/FilterSearchInput';
import TransparentSpinner from 'components/spinners/TransparentSpinner';
import { refreshRate } from 'constants/AppConstants';
import Roles from 'constants/Roles';
import Routes from 'constants/Routes';
import useMe from 'contexts/authentication/useMe';
import { useAssignmentsFilters } from 'contexts/assignmentsFilters/useAssignmentsFilters';
import useUpdateInterval from 'hooks/useUpdateInterval';
import BulkMessages from 'pages/GlobalSearch/BulkMessages';
import BulkOperations from 'pages/GlobalSearch/BulkOperations';
import useAssignmentsBulkOperations from 'pages/GlobalSearch/useAssignmentsBulkOperations';
import { FC, useMemo } from 'react';
import {
  generatePath,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { RequestStatus, useResponseFn } from 'hooks/useApiCallFn';
import styled from 'styled-components';
import assignmentsSearchFunction from 'utils/old_assignmentsSearchFunction';

const FilterRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  margin: 10px 10px;
`;

const SearchInputs = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin-left: auto;
`;

const Horizontal = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

/**
 * VIKTIGT — arbetslista över alla ärenden markerade som viktiga (Case.IsImportant), oavsett datum.
 * Flaggan nollställs automatiskt när ett uppdrag på ärendet planeras, så listan innehåller de ännu
 * ej planerade viktiga ärendena. Ärendet behåller sin riktiga ärendetyp och syns även under den.
 */
const ImportantCasesPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    state: {
      visibleStatuses,
      visibleBusinessUnits,
      searchText,
      triggerAssignmentsUpdate,
    },
  } = useAssignmentsFilters();
  const me = useMe();
  const userIsGoSupport = me?.roles.includes(Roles.GoSupport);

  const getImportant = useAssignmentGetImportantHook();
  const importantAssignments = useResponseFn(
    () => getImportant(),
    [triggerAssignmentsUpdate]
  );

  useUpdateInterval(() => importantAssignments.update(), refreshRate, true);

  const filteredAssignments = useMemo(
    () =>
      assignmentsSearchFunction(
        importantAssignments.response ?? [],
        searchText,
        visibleStatuses,
        visibleBusinessUnits,
        location.pathname
      ),
    [
      importantAssignments.response,
      location.pathname,
      searchText,
      visibleBusinessUnits,
      visibleStatuses,
    ]
  );

  const handleAssignmentItemClick = (model: AssignmentViewModel) => {
    navigate(
      generatePath(Routes.viktigt.case, { id: String(model.case.caseID) })
    );
  };

  const assignmentsBulkOperations = useAssignmentsBulkOperations(
    filteredAssignments ?? [],
    () => importantAssignments.update()
  );

  const bulkMessagesButton = userIsGoSupport ? (
    <BulkMessages bulkOperations={assignmentsBulkOperations} />
  ) : null;

  const renderList = () => {
    if (importantAssignments.status === RequestStatus.Error) {
      return <ErrorTryAgain onTryAgain={() => importantAssignments.update()} />;
    }

    if (!importantAssignments.response) {
      return <TransparentSpinner />;
    }

    return (
      <>
        {!!filteredAssignments.length && userIsGoSupport && (
          <Horizontal>
            <BulkOperations
              bulkActions={[bulkMessagesButton]}
              bulkOperations={assignmentsBulkOperations}
              handleAssignmentItemClick={(model: AssignmentExtendedModel) =>
                handleAssignmentItemClick(model)
              }
            />
          </Horizontal>
        )}
        <AssignmentsList
          assignments={filteredAssignments}
          bulkOperations={assignmentsBulkOperations}
          loading={importantAssignments.status === RequestStatus.Fetching}
          onAssignmentItemClick={handleAssignmentItemClick}
          onAssignmentUpdated={() => importantAssignments.update()}
          showStatusColors={visibleStatuses === null}
        />
      </>
    );
  };

  return (
    <>
      <Outlet />
      <FilterRow>
        <FilterPicker />
        <SearchInputs>
          <FilterSearchInput />
        </SearchInputs>
      </FilterRow>
      {renderList()}
    </>
  );
};

export default ImportantCasesPage;
